(() => {
  if (window.__dsivVideoStageInitialized) return;
  window.__dsivVideoStageInitialized = true;

  const desktop = window.matchMedia('(min-width: 801px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const stages = new Set();
  let activeStage = null;
  let activeAudioStage = null;
  let resumeStage = null;

  const updateButton = (stage, paused) => {
    const button = stage.querySelector('[data-dsiv-video-stage-toggle]');
    if (!button) return;
    const label = paused ? stage.dataset.dsivPlayLabel : stage.dataset.dsivPauseLabel;
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
    stage.classList.toggle('is-paused', paused);
  };

  const loadVideo = (stage) => {
    const video = stage.querySelector('[data-dsiv-video-stage-media]');
    if (!video || video.dataset.loaded === 'true') return video;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.dataset.loaded = 'true';
    video.load();
    return video;
  };

  const updateAudioButton = (stage, playing) => {
    const button = stage.querySelector('[data-dsiv-video-stage-audio-toggle]');
    const label = stage.querySelector('[data-dsiv-video-stage-audio-label]');
    if (!button || !label) return;
    const text = playing ? stage.dataset.dsivAudioPauseLabel : stage.dataset.dsivAudioPlayLabel;
    button.setAttribute('aria-label', text);
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    label.textContent = text;
    stage.classList.toggle('is-audio-playing', playing);
  };

  const pauseStageAudio = (stage, reset = false) => {
    const audio = stage.querySelector('[data-dsiv-video-stage-audio]');
    if (!audio) return;
    audio.pause();
    if (reset) audio.currentTime = 0;
    updateAudioButton(stage, false);
    if (activeAudioStage === stage) activeAudioStage = null;
  };

  const pauseVideo = (stage, userInitiated = false) => {
    const video = stage.querySelector('[data-dsiv-video-stage-media]');
    if (!video) return;
    if (userInitiated) stage.dataset.dsivUserPaused = 'true';
    video.pause();
    pauseStageAudio(stage);
    updateButton(stage, true);
    if (activeStage === stage) activeStage = null;
  };

  const playVideo = async (stage, userInitiated = false) => {
    if (!desktop.matches || reducedMotion.matches || stage.dataset.inViewport !== 'true') {
      pauseVideo(stage);
      return;
    }

    if (!userInitiated && stage.dataset.dsivUserPaused === 'true') return;

    const video = loadVideo(stage);
    if (!video) return;

    stages.forEach((otherStage) => {
      if (otherStage !== stage) pauseVideo(otherStage);
    });

    try {
      if (userInitiated) stage.dataset.dsivUserPaused = 'false';
      await video.play();
      activeStage = stage;
      updateButton(stage, false);
    } catch (error) {
      pauseVideo(stage);
    }
  };

  const playStageAudio = async (stage) => {
    const audio = stage.querySelector('[data-dsiv-video-stage-audio]');
    if (!audio) return;

    document.querySelectorAll('audio').forEach((otherAudio) => {
      if (otherAudio !== audio) otherAudio.pause();
    });
    stages.forEach((otherStage) => {
      if (otherStage !== stage) {
        pauseStageAudio(otherStage, true);
        pauseVideo(otherStage);
      }
    });

    const canPlayMotion = desktop.matches && !reducedMotion.matches && stage.dataset.inViewport === 'true';
    const video = canPlayMotion ? loadVideo(stage) : null;

    try {
      audio.currentTime = 0;
      if (video) {
        video.pause();
        video.currentTime = 0;
        stage.dataset.dsivUserPaused = 'false';
        video.play().then(() => {
          activeStage = stage;
          updateButton(stage, false);
        }).catch(() => {
          updateButton(stage, true);
        });
      }
      await audio.play();
      activeAudioStage = stage;
      updateAudioButton(stage, true);
    } catch (error) {
      stage.dataset.dsivAudioError = error?.name || 'playback-error';
      pauseStageAudio(stage, true);
    }
  };

  const setupStage = (stage) => {
    if (stage.dataset.initialized === 'true') return;
    stage.dataset.initialized = 'true';
    stages.add(stage);

    const video = stage.querySelector('[data-dsiv-video-stage-media]');
    const button = stage.querySelector('[data-dsiv-video-stage-toggle]');
    const audio = stage.querySelector('[data-dsiv-video-stage-audio]');
    const audioButton = stage.querySelector('[data-dsiv-video-stage-audio-toggle]');
    if (!video || !button) return;

    const shouldAutoplay = stage.dataset.dsivAutoplay !== 'false';
    stage.dataset.dsivUserPaused = 'false';

    video.addEventListener('loadeddata', () => {
      stage.classList.add('is-video-ready');
      button.hidden = false;
    });

    video.addEventListener('ended', () => {
      pauseVideo(stage);
      video.currentTime = 0;
    });

    button.addEventListener('click', () => {
      if (video.paused) {
        playVideo(stage, true);
      } else {
        pauseVideo(stage, true);
      }
    });

    if (audio && audioButton) {
      updateAudioButton(stage, false);
      audioButton.addEventListener('click', () => {
        if (audio.paused) {
          playStageAudio(stage);
        } else {
          pauseStageAudio(stage);
        }
      });
      audio.addEventListener('play', () => updateAudioButton(stage, true));
      audio.addEventListener('pause', () => updateAudioButton(stage, false));
      audio.addEventListener('ended', () => {
        pauseStageAudio(stage, true);
        video.currentTime = 0;
        if (stage.dataset.dsivAutoplay !== 'false' && stage.dataset.inViewport === 'true') {
          playVideo(stage);
        }
      });
    }

    if (!shouldAutoplay) {
      button.hidden = false;
      updateButton(stage, true);
    }

    observer.observe(stage);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const stage = entry.target;
      const shouldAutoplay = stage.dataset.dsivAutoplay !== 'false';
      stage.dataset.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.2 ? 'true' : 'false';
      if (stage.dataset.inViewport === 'true' && shouldAutoplay) {
        playVideo(stage);
      } else {
        pauseVideo(stage);
      }
    });
  }, { threshold: [0, 0.2, 0.6] });

  const refresh = () => {
    document.querySelectorAll('[data-dsiv-video-stage]').forEach(setupStage);
    stages.forEach((stage) => {
      const shouldAutoplay = stage.dataset.dsivAutoplay !== 'false';
      if (desktop.matches && !reducedMotion.matches && shouldAutoplay) {
        playVideo(stage);
      } else {
        pauseVideo(stage);
      }
    });

    document.querySelectorAll('audio').forEach((audio) => {
      if (audio.matches('[data-dsiv-video-stage-audio]')) return;
      if (audio.dataset.dsivVideoStageWired === 'true') return;
      audio.dataset.dsivVideoStageWired = 'true';
      audio.addEventListener('play', () => {
        stages.forEach((stage) => {
          pauseStageAudio(stage);
          pauseVideo(stage);
        });
      });
    });
  };

  desktop.addEventListener('change', refresh);
  reducedMotion.addEventListener('change', refresh);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      resumeStage = activeStage;
      stages.forEach((stage) => {
        pauseStageAudio(stage);
        pauseVideo(stage);
      });
    } else if (resumeStage) {
      playVideo(resumeStage);
      resumeStage = null;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();
